"use client";

import * as React from "react";
import * as LabelPrimitive from "@radix-ui/react-label";
import { Slot } from "@radix-ui/react-slot";
import {
  Controller,
  FormProvider,
  useFormContext,
  useFormState,
  type ControllerProps,
  type FieldPath,
  type FieldValues,
} from "react-hook-form";

import { cn } from "@/ui/cn";
import { Label } from "./label";
import { Text } from "./text";

const Form = FormProvider;

type FormFieldContextValue<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  name: TName;
};

const FormFieldContext = React.createContext<FormFieldContextValue>({} as FormFieldContextValue);

const FormField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  ...props
}: ControllerProps<TFieldValues, TName>) => {
  return (
    <FormFieldContext.Provider value={{ name: props.name }}>
      <Controller {...props} />
    </FormFieldContext.Provider>
  );
};

const useFormField = () => {
  const fieldContext = React.useContext(FormFieldContext);
  const itemContext = React.useContext(FormItemContext);
  const { getFieldState } = useFormContext();
  const formState = useFormState({ name: fieldContext.name });
  const fieldState = getFieldState(fieldContext.name, formState);

  if (!fieldContext) {
    throw new Error("useFormField should be used within <FormField>");
  }

  const { id, required } = itemContext;

  return {
    id,
    required,
    name: fieldContext.name,
    formItemId: `${id}-form-item`,
    formLabelId: `${id}-form-item-label`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    ...fieldState,
  };
};

type FormItemContextValue = {
  id: string;
  required?: boolean;
};

export const FormItemContext = React.createContext<FormItemContextValue>(
  {} as FormItemContextValue
);

function FormItem({
  className,
  orientation,
  required,
  ...props
}: React.ComponentProps<"div"> & {
  orientation?: "compact" | "stacked" | "inline" | "spread";
  /** Marks the label with an asterisk and tells assistive tech the control must be filled in. */
  required?: boolean;
}) {
  const id = React.useId();

  return (
    <FormItemContext.Provider value={{ id, required }}>
      <div
        data-slot="form-item"
        className={cn(
          orientation === "compact" &&
            "@container grid gap-x-3 gap-y-1 @[256px]:grid-cols-[auto_1fr] @[256px]:items-center",
          orientation === "inline" && "grid grid-cols-[auto_1fr] items-center gap-x-3 gap-y-1",
          orientation === "spread" && "flex flex-row items-center justify-between gap-x-3",
          (!orientation || orientation === "stacked") && "grid gap-2",
          className
        )}
        {...props}
      />
    </FormItemContext.Provider>
  );
}

function FormLabel({
  className,
  children,
  ...props
}: React.ComponentProps<typeof LabelPrimitive.Root>) {
  const { error, formItemId, formLabelId, required } = useFormField();

  return (
    <Label
      data-slot="form-label"
      data-error={!!error}
      className={cn("data-[error=true]:text-destructive-text", required && "gap-1.5", className)}
      id={formLabelId}
      htmlFor={formItemId}
      {...props}
    >
      {children}
      {/* aria-hidden: FormControl's aria-required carries this to assistive tech. */}
      {required && (
        <span aria-hidden className="text-destructive-text">
          *
        </span>
      )}
    </Label>
  );
}

function FormControl({ ...props }: React.ComponentProps<typeof Slot>) {
  const { error, formItemId, formDescriptionId, formMessageId, required } = useFormField();

  return (
    <Slot
      data-slot="form-control"
      id={formItemId}
      aria-describedby={!error ? `${formDescriptionId}` : `${formDescriptionId} ${formMessageId}`}
      aria-invalid={!!error}
      aria-required={required || undefined}
      {...props}
    />
  );
}

/**
 * `FormControl` for a control that renders a group rather than a labelable element — a segmented
 * control's `radiogroup`, say. `htmlFor` can't name one of those, so the association runs the
 * other way, from the group to the label.
 */
function FormGroupControl({ ...props }: React.ComponentProps<typeof Slot>) {
  const { formLabelId } = useFormField();

  return <FormControl aria-labelledby={formLabelId} {...props} />;
}

function FormDescription({ className, ...props }: React.ComponentPropsWithoutRef<typeof Text>) {
  const { formDescriptionId } = useFormField();

  return (
    <Text
      data-slot="form-description"
      id={formDescriptionId}
      size="sm"
      color="muted"
      className={className}
      {...props}
    />
  );
}

function FormMessage({ className, ...props }: React.ComponentPropsWithoutRef<typeof Text>) {
  const { error, formMessageId } = useFormField();
  const body = error ? String(error?.message ?? "") : props.children;

  if (!body) {
    return null;
  }

  return (
    <Text
      data-slot="form-message"
      id={formMessageId}
      size="sm"
      className={cn("text-destructive-text col-span-full", className)}
      {...props}
    >
      {body}
    </Text>
  );
}

export { FormHint } from "./form-hint";
export { FormSection } from "./form-section";

export {
  useFormField,
  Form,
  FormItem,
  FormLabel,
  FormControl,
  FormGroupControl,
  FormDescription,
  FormMessage,
  FormField,
};
